import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { bankingAPI } from '../../api';
import Layout from '../../components/Layout';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Textarea } from '../../components/ui/Textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';

const TransactionEditor = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;
  const isTransferMode = window.location.search.includes('transfer=true');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [accounts, setAccounts] = useState([]);

  const [formData, setFormData] = useState({
    transaction_type: isTransferMode ? 'transfer' : 'deposit',
    bank_account_id: '',
    to_account_id: '', // For transfers
    amount: '',
    transaction_date: new Date().toISOString().split('T')[0],
    reference: '',
    description: '',
    notes: '',
  });

  useEffect(() => {
    fetchAccounts();
    if (isEditMode) {
      // Note: Transactions can't be fully edited if linked to invoices/purchases
      // For now, we'll only allow editing manual transactions
      // You might want to show a read-only view for linked transactions
    }
  }, [id]);

  const fetchAccounts = async () => {
    try {
      const data = await bankingAPI.getAccounts({ include_inactive: false });
      setAccounts(data.filter(acc => acc.is_active));
    } catch (err) {
      setError(err.message || 'Failed to load accounts');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.bank_account_id) {
      setError('Account is required');
      return;
    }

    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      setError('Amount must be greater than 0');
      return;
    }

    if (formData.transaction_type === 'transfer' && !formData.to_account_id) {
      setError('Destination account is required for transfers');
      return;
    }

    if (formData.transaction_type === 'transfer' && formData.bank_account_id === formData.to_account_id) {
      setError('Cannot transfer to the same account');
      return;
    }

    try {
      setSaving(true);
      setError('');

      const payload = {
        ...formData,
        amount: parseFloat(formData.amount),
      };

      if (isEditMode) {
        await bankingAPI.updateTransaction(id, payload);
      } else if (formData.transaction_type === 'transfer') {
        await bankingAPI.createTransfer({
          from_account_id: formData.bank_account_id,
          to_account_id: formData.to_account_id,
          amount: payload.amount,
          transaction_date: payload.transaction_date,
          reference: payload.reference,
          notes: payload.notes,
        });
      } else {
        await bankingAPI.createTransaction(payload);
      }

      navigate('/tenant/banking');
    } catch (err) {
      setError(err.message || 'Failed to save transaction');
      console.error('Error saving transaction:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
    }).format(amount || 0);
  };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Card>
          <CardHeader>
            <CardTitle className="text-display text-[var(--ds-text-primary)]">
              {isTransferMode ? 'New Transfer' : isEditMode ? 'Edit Transaction' : 'New Transaction'}
            </CardTitle>
          </CardHeader>

          {loading ? (
            <CardContent className="p-8 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--ds-accent-primary)]"></div>
              <p className="mt-2 text-xs text-[var(--ds-text-muted)]">Loading transaction...</p>
            </CardContent>
          ) : (
            <form onSubmit={handleSubmit}>
              <CardContent className="p-6 space-y-6">
              {error && (
                <div className="bg-[var(--ds-status-danger-bg)] border border-[var(--ds-status-danger)]/30 rounded-[var(--ds-radius-md)] px-4 py-3">
                  <p className="text-xs text-[var(--ds-status-danger)]">{error}</p>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Transaction Type */}
                <div>
                  <Select
                    label="Transaction Type"
                    name="transaction_type"
                    value={formData.transaction_type}
                    onChange={handleChange}
                    disabled={isEditMode || isTransferMode}
                    required
                    requiredIndicator
                    className="h-8"
                  >
                    <option value="deposit">Deposit</option>
                    <option value="withdrawal">Withdrawal</option>
                    <option value="transfer">Transfer</option>
                    <option value="manual">Manual</option>
                  </Select>
                </div>

                {/* Transaction Date */}
                <div>
                  <Input
                    label="Date"
                    type="date"
                    name="transaction_date"
                    value={formData.transaction_date}
                    onChange={handleChange}
                    required
                    requiredIndicator
                    className="h-8"
                  />
                </div>

                {/* From Account (or Account for non-transfers) */}
                {formData.transaction_type !== 'transfer' ? (
                  <div className="md:col-span-2">
                    <Select
                      label="Account"
                      name="bank_account_id"
                      value={formData.bank_account_id}
                      onChange={handleChange}
                      required
                      requiredIndicator
                      className="h-8"
                    >
                      <option value="">Select an account</option>
                      {accounts.map(account => (
                        <option key={account.id} value={account.id}>
                          {account.account_name} ({formatCurrency(account.current_balance)})
                        </option>
                      ))}
                    </Select>
                  </div>
                ) : (
                  <>
                    <div>
                      <Select
                        label="From Account"
                        name="bank_account_id"
                        value={formData.bank_account_id}
                        onChange={handleChange}
                        required
                        requiredIndicator
                        className="h-8"
                      >
                        <option value="">Select account</option>
                        {accounts.map(account => (
                          <option key={account.id} value={account.id}>
                            {account.account_name} ({formatCurrency(account.current_balance)})
                          </option>
                        ))}
                      </Select>
                    </div>
                    <div>
                      <Select
                        label="To Account"
                        name="to_account_id"
                        value={formData.to_account_id}
                        onChange={handleChange}
                        required
                        requiredIndicator
                        className="h-8"
                      >
                        <option value="">Select account</option>
                        {accounts
                          .filter(acc => acc.id !== formData.bank_account_id)
                          .map(account => (
                            <option key={account.id} value={account.id}>
                              {account.account_name} ({formatCurrency(account.current_balance)})
                            </option>
                          ))}
                      </Select>
                    </div>
                  </>
                )}

                {/* Amount */}
                <div>
                  <Input
                    label="Amount"
                    type="number"
                    name="amount"
                    value={formData.amount}
                    onChange={handleChange}
                    step="0.01"
                    min="0.01"
                    required
                    placeholder="0.00"
                    requiredIndicator
                    className="h-8"
                  />
                </div>

                {/* Reference */}
                <div>
                  <Input
                    label="Reference"
                    type="text"
                    name="reference"
                    value={formData.reference}
                    onChange={handleChange}
                    placeholder="Transaction reference"
                    className="h-8"
                  />
                </div>

                {/* Description */}
                <div className="md:col-span-2">
                  <Input
                    label="Description"
                    type="text"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    placeholder="Brief description of the transaction"
                    className="h-8"
                  />
                </div>

                {/* Notes */}
                <div className="md:col-span-2">
                  <Textarea
                    label="Notes"
                    name="notes"
                    value={formData.notes}
                    onChange={handleChange}
                    rows={3}
                    placeholder="Additional notes..."
                    className="text-xs"
                  />
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end space-x-3 pt-6 border-t border-[var(--ds-border-divider)]">
                <Button
                  variant="outline"
                  size="sm"
                  type="button"
                  onClick={() => navigate('/tenant/banking')}
                  disabled={saving}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  type="submit"
                  disabled={saving}
                >
                  {saving ? 'Saving...' : isEditMode ? 'Update Transaction' : 'Create Transaction'}
                </Button>
              </div>
              </CardContent>
            </form>
          )}
        </Card>
      </div>
    </Layout>
  );
};

export default TransactionEditor;

